import { progressPct } from "@/lib/progress";
import { Fragment, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Flag, Pencil, Plus, Link2, Unlink, GripVertical, ChevronsDown, ChevronsUp, RotateCcw, Columns3, ShieldAlert, Copy, IndentIncrease, IndentDecrease, CheckCircle2, Layers, Trash2, ArrowUp, ArrowDown, FolderInput, BookMarked } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { statusMeta, priorityMeta, trackMeta } from "@/lib/constants";
import SwatchPicker from "@/components/SwatchPicker";
import { toast } from "sonner";

const isDone = (t) => t.health === "blue" || t.status === "done" || (t.percent_complete || 0) >= 100;
const effPct = (t) => (t.percent_complete != null ? Math.max(0, Math.min(100, t.percent_complete)) : (isDone(t) ? 100 : 0));
const todayISO = () => new Date().toISOString().slice(0, 10);

function durationOf(t) {
  if (t.is_milestone) return "0d";
  if (t.start_date && t.end_date) {
    const s = new Date(t.start_date + "T00:00:00"), e = new Date(t.end_date + "T00:00:00");
    if (e < s) return "—";
    let n = 0;
    for (const d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      const w = d.getDay();
      if (w !== 0 && w !== 6) n++;
    }
    return n > 0 ? `${n}d` : "1d";
  }
  if (t.effort_days != null) return `${t.effort_days}d`;
  return "—";
}

const bizDaysCount = (sIso, eIso) => {
  const s = new Date(sIso + "T00:00:00"), e = new Date(eIso + "T00:00:00");
  if (e < s) return "";
  let n = 0;
  for (const d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
    const w = d.getDay();
    if (w !== 0 && w !== 6) n++;
  }
  return n || 1;
};

const pad2 = (n) => String(n).padStart(2, "0");
const fmtD = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return `${d.toLocaleDateString("en-US", { weekday: "short" })} ${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}`;
};
const addBusinessDays = (startIso, n) => {
  const d = new Date(startIso + "T00:00:00");
  let c = 1;
  while (c < n) {
    d.setDate(d.getDate() + 1);
    const w = d.getDay();
    if (w !== 0 && w !== 6) c++;
  }
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

function PctBar({ value }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 rounded-full bg-secondary overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${value}%`, background: value >= 100 ? "#2563EB" : "#10B981" }} />
      </div>
      <span className="text-xs tabular-nums w-9 text-right">{value}%</span>
    </div>
  );
}

const COLUMNS = {
  id: { label: "ID", width: "w-16" },
  task: { label: "Milestone / Task", width: "" },
  start: { label: "Start", width: "w-32" },
  end: { label: "End", width: "w-32" },
  duration: { label: "Duration", width: "w-20" },
  resource: { label: "Resource", width: "w-32" },
  pct: { label: "% Complete", width: "w-36" },
  trueup: { label: "True-up", width: "w-24" },
  status: { label: "Status", width: "w-24" },
  priority: { label: "Priority", width: "w-24" },
  links: { label: "Links", width: "w-20" },
};
const DEFAULT_ORDER = ["id", "task", "start", "end", "duration", "resource", "pct", "trueup", "status", "priority", "links"];
const COL_ORDER_KEY = "wbs-col-order-v2";
const COL_HIDDEN_KEY = "wbs-col-hidden-v2";
const COL_MIME = "application/x-wbs-col";

function loadColOrder() {
  try {
    const saved = JSON.parse(localStorage.getItem(COL_ORDER_KEY));
    if (Array.isArray(saved) && saved.length === DEFAULT_ORDER.length && DEFAULT_ORDER.every((k) => saved.includes(k))) return saved;
  } catch (e) { /* ignore */ }
  return DEFAULT_ORDER;
}

function loadHidden() {
  try {
    const saved = JSON.parse(localStorage.getItem(COL_HIDDEN_KEY));
    if (Array.isArray(saved)) return saved.filter((k) => DEFAULT_ORDER.includes(k) && k !== "task");
  } catch (e) { /* ignore */ }
  return [];
}

export default function WbsTable({ phases, tasks, phaseColors, collapsed, onToggle, onOpenTask, onInlineUpdate, onAddTask, onLinkTask, onDropTask, onTrueUp, onDuplicate, onRenamePhase, onAddPhase, onPhaseColor, onMovePhase, onDuplicatePhase, onSaveTemplate, onImportPhase, onDeletePhase, onBulkMove, onBulkLink, onBulkUnlink, onDelete, summaryColor, onSummaryColor, progressStyle }) {
  const today = todayISO();
  const [edit, setEdit] = useState(null); // { id, field: 'pct'|'start'|'end' }
  const [draft, setDraft] = useState("");
  const [dragId, setDragId] = useState(null);
  const [hover, setHover] = useState(null); // { id, mode: 'before' | 'nest' }
  const [colOrder, setColOrder] = useState(loadColOrder);
  const [hiddenCols, setHiddenCols] = useState(loadHidden);
  const [dragCol, setDragCol] = useState(null);
  const [hoverCol, setHoverCol] = useState(null);
  const [collapsedIds, setCollapsedIds] = useState(() => new Set());
  const [phaseEdit, setPhaseEdit] = useState(null);
  const [phaseDraft, setPhaseDraft] = useState("");
  const [addingPhase, setAddingPhase] = useState(false);
  const [newPhase, setNewPhase] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [msOnly, setMsOnly] = useState(false);
  const sumColor = summaryColor || "#FDE68A";
  const parentIds = () => tasks.filter((t) => tasks.some((k) => k.parent_id === t.id)).map((t) => t.id);
  const expandAllRows = () => setCollapsedIds(new Set());
  const collapseAllRows = () => setCollapsedIds(new Set(parentIds()));
  const collapsePhaseRows = (g, collapse) => setCollapsedIds((s) => {
    const n = new Set(s);
    tasks.filter((t) => t.phase === g && tasks.some((k) => k.parent_id === t.id)).forEach((t) => (collapse ? n.add(t.id) : n.delete(t.id)));
    return n;
  });
  const visibleOrder = colOrder.filter((k) => !hiddenCols.includes(k));

  const toggleSelect = (tid) => setSelected((s) => { const n = new Set(s); if (n.has(tid)) n.delete(tid); else n.add(tid); return n; });
  const rowClick = (e, t) => { if (e.ctrlKey || e.metaKey) { e.preventDefault(); toggleSelect(t.id); } else onOpenTask(t); };
  const orderedSelected = tasks.filter((t) => selected.has(t.id)).map((t) => t.id);
  const clearSelection = () => setSelected(new Set());

  const toggleRow = (rid) => setCollapsedIds((s) => { const n = new Set(s); if (n.has(rid)) n.delete(rid); else n.add(rid); return n; });
  const indent = (e, t, siblings, idx) => {
    e.stopPropagation();
    if (idx <= 0) return;
    const target = siblings[idx - 1];
    if (!target || target.id === t.parent_id) return;
    onInlineUpdate?.(t, { parent_id: target.id });
  };
  const outdent = (e, t) => {
    e.stopPropagation();
    if (!t.parent_id) return;
    const parent = tasks.find((x) => x.id === t.parent_id);
    onInlineUpdate?.(t, { parent_id: parent?.parent_id || null });
  };
  const commitPhaseRename = (g) => {
    const v = phaseDraft.trim();
    setPhaseEdit(null);
    if (v && v !== g) onRenamePhase?.(g, v);
  };
  const submitNewPhase = () => {
    const v = newPhase.trim();
    setAddingPhase(false);
    setNewPhase("");
    if (v) onAddPhase?.(v);
  };

  const toggleCol = (key) => {
    setHiddenCols((h) => {
      const next = h.includes(key) ? h.filter((k) => k !== key) : [...h, key];
      localStorage.setItem(COL_HIDDEN_KEY, JSON.stringify(next));
      return next;
    });
  };

  const saveColOrder = (order) => {
    setColOrder(order);
    localStorage.setItem(COL_ORDER_KEY, JSON.stringify(order));
  };
  const dropCol = (targetKey) => {
    if (!dragCol || dragCol === targetKey) return;
    const order = colOrder.filter((k) => k !== dragCol);
    order.splice(order.indexOf(targetKey), 0, dragCol);
    saveColOrder(order);
  };

  let msCounter = 0;
  const msIds = useMemo(() => {
    const m = {};
    tasks.filter((t) => t.is_milestone).forEach((t) => { m[t.id] = `M${++msCounter}`; });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  const childrenOf = (id) => tasks.filter((t) => t.parent_id === id);
  const rowPct = (t) => {
    if (isDone(t)) return 100;
    const kids = childrenOf(t.id);
    if (kids.length) return Math.round(kids.reduce((a, k) => a + effPct(k), 0) / kids.length);
    return effPct(t);
  };

  const beginEdit = (e, t, field, initial) => {
    e.stopPropagation();
    if (e.ctrlKey || e.metaKey) { e.preventDefault(); toggleSelect(t.id); return; }
    setEdit({ id: t.id, field });
    setDraft(initial == null ? "" : String(initial));
  };
  const cancel = () => { setEdit(null); setDraft(""); };
  const commit = (t) => {
    if (!edit) return;
    const field = edit.field;
    let patch = {};
    if (field === "pct") {
      patch = { percent_complete: draft === "" ? null : Math.max(0, Math.min(100, Number(draft))) };
    } else if (field === "start") {
      patch = { start_date: draft || null };
      if (t.is_milestone) patch.end_date = draft || null;
    } else if (field === "end") {
      patch = { end_date: draft || null };
    } else if (field === "duration") {
      if (draft === "") { cancel(); return; }
      const n = Math.max(1, parseInt(draft, 10) || 1);
      if (t.start_date) {
        patch = { end_date: addBusinessDays(t.start_date, n) };
      }
    } else if (field === "trueup") {
      const pct = Math.max(0, Math.min(100, Number(draft) || 0));
      cancel();
      onTrueUp?.(t, pct);
      return;
    }
    cancel();
    onInlineUpdate?.(t, patch);
  };
  const editKeys = (e, t) => {
    if (e.key === "Enter") { e.preventDefault(); commit(t); }
    else if (e.key === "Escape") { e.preventDefault(); cancel(); }
  };
  const isEditing = (t, field) => edit && edit.id === t.id && edit.field === field;

  const renderCell = (key, ctx) => {
    const { t, id, depth, color, kids, leaf, overdue, pct, sm, pm, dateCls, draggable } = ctx;
    switch (key) {
      case "id": return (
        <td key={key} className="px-3 py-2 text-xs font-mono text-muted-foreground whitespace-nowrap cursor-pointer" onClick={(e) => rowClick(e, t)}>
          <span className="inline-flex items-center gap-1.5">
            {onBulkMove && (
              <input type="checkbox" checked={selected.has(t.id)} onClick={(e) => e.stopPropagation()} onChange={() => toggleSelect(t.id)}
                className="h-3.5 w-3.5 accent-blue-600 cursor-pointer" title="Select to move multiple rows together" data-testid={`wbs-select-${t.id}`} />
            )}
            {draggable && <GripVertical className="h-3 w-3 text-muted-foreground/50 cursor-grab" data-testid={`wbs-grip-${t.id}`} />}
            {id}
          </span>
        </td>
      );
      case "task": {
        const { siblings, idx, isSummary, isCollapsed } = ctx;
        const actions = (
          <span className="ml-auto inline-flex items-center gap-0.5 opacity-0 group-hover/wbsrow:opacity-100 transition-opacity shrink-0">
            {onInlineUpdate && t.parent_id && (
              <button type="button" title="Outdent" onClick={(e) => outdent(e, t)} className="p-0.5 rounded hover:bg-secondary" data-testid={`wbs-outdent-${t.id}`}><IndentDecrease className="h-3.5 w-3.5 text-muted-foreground" /></button>
            )}
            {onInlineUpdate && idx > 0 && (
              <button type="button" title="Indent under the row above" onClick={(e) => indent(e, t, siblings, idx)} className="p-0.5 rounded hover:bg-secondary" data-testid={`wbs-indent-${t.id}`}><IndentIncrease className="h-3.5 w-3.5 text-muted-foreground" /></button>
            )}
            {onDuplicate && (
              <button type="button" title="Open task details" onClick={(e) => { e.stopPropagation(); onOpenTask(t); }} className="p-0.5 rounded hover:bg-secondary" data-testid={`wbs-open-${t.id}`}><Pencil className="h-3.5 w-3.5 text-muted-foreground" /></button>
            )}
            {onDuplicate && (
              <button type="button" title="Duplicate this row" onClick={(e) => { e.stopPropagation(); onDuplicate(t); }} className="p-0.5 rounded hover:bg-secondary" data-testid={`wbs-duplicate-${t.id}`}><Copy className="h-3.5 w-3.5 text-muted-foreground" /></button>
            )}
            {onDelete && (
              <button type="button" title="Delete this row" onClick={(e) => { e.stopPropagation(); onDelete(t); }} className="p-0.5 rounded hover:bg-red-100" data-testid={`wbs-delete-${t.id}`}><Trash2 className="h-3.5 w-3.5 text-red-400" /></button>
            )}
          </span>
        );
        const badges = (
          <>
            {t.is_launch && <span className="text-amber-500 text-sm" title="Launch">★</span>}
            {["at_risk", "jeopardy"].includes(t.track_status) && (() => { const tm = trackMeta(t.track_status); return (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0" style={{ color: tm.color, background: `${tm.color}1a` }} title={`Track status: ${tm.label}`} data-testid={`wbs-track-${t.id}`}>{tm.label}</span>
            ); })()}
            {(() => {
              if (t.is_milestone || t.status === "done" || t.linked_risk_id || ["at_risk", "jeopardy"].includes(t.track_status)) return null;
              let behind = overdue;
              if (!behind && t.start_date && t.end_date) {
                const today = new Date().toISOString().slice(0, 10);
                if (t.start_date <= today && t.end_date >= today) {
                  const exp = Math.min(100, Math.round((Date.parse(today) - Date.parse(t.start_date)) / Math.max(1, Date.parse(t.end_date) - Date.parse(t.start_date)) * 100));
                  behind = exp - (t.percent_complete || 0) >= 20;
                }
              }
              if (!behind) return null;
              return (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 bg-amber-100 text-amber-700 inline-flex items-center gap-0.5"
                      title="This task is tracking behind — open it to flag At Risk and link a risk"
                      data-testid={`wbs-needs-risk-${t.id}`}>
                  <ShieldAlert className="h-2.5 w-2.5" /> needs risk
                </span>
              );
            })()}
          </>
        );
        const chevron = kids.length > 0 && (
          <button type="button" onClick={(e) => { e.stopPropagation(); toggleRow(t.id); }} className="shrink-0 mt-0.5" title={isCollapsed ? "Expand" : "Collapse"} data-testid={`wbs-collapse-${t.id}`}>
            {isCollapsed ? <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
          </button>
        );
        const doneRow = t.status === "done" || (t.percent_complete || 0) >= 100;
        const nameEl = <span className={`break-words min-w-0 leading-snug ${isSummary || t.is_milestone ? "text-[13px] font-semibold" : "text-xs"}`}>{doneRow && <CheckCircle2 className="h-3.5 w-3.5 inline-block mr-1 -mt-0.5 text-emerald-500" data-testid={`wbs-done-check-${t.id}`} />}{t.title}</span>;
        if (isSummary) return (
          <td key={key} colSpan={2} className="px-3 py-2 cursor-pointer" onClick={(e) => rowClick(e, t)}>
            <div className="flex items-start gap-2" style={{ paddingLeft: depth * 18 }}>
              {chevron || <Layers className="h-3.5 w-3.5 shrink-0 mt-0.5 text-muted-foreground" />}
              {nameEl}
              {kids.length > 0 && <span className="text-[10px] text-muted-foreground mt-0.5 shrink-0">({kids.length})</span>}
              {badges}
              {actions}
            </div>
          </td>
        );
        if (t.is_milestone) return (
          <Fragment key={key}>
            <td className="px-3 py-2 cursor-pointer" onClick={(e) => rowClick(e, t)}>
              <div className="flex items-start gap-2" style={{ paddingLeft: depth * 18 }}>
                {chevron}
                <Flag className="h-3.5 w-3.5 shrink-0 mt-0.5" style={{ color: t.ms_color || color }} fill={t.ms_color || color} />
                {nameEl}
                {badges}
                {actions}
              </div>
            </td>
            <td className="px-3 py-2 cursor-pointer" onClick={(e) => rowClick(e, t)} />
          </Fragment>
        );
        return (
          <Fragment key={key}>
            <td className="px-3 py-2 cursor-pointer" onClick={(e) => rowClick(e, t)} />
            <td className="px-3 py-2 cursor-pointer" onClick={(e) => rowClick(e, t)}>
              <div className="flex items-start gap-2" style={{ paddingLeft: depth * 18 }}>
                {chevron}
                <span className="h-1.5 w-1.5 rounded-full shrink-0 mt-1.5" style={{ background: color }} />
                {nameEl}
                {badges}
                {actions}
              </div>
            </td>
          </Fragment>
        );
      }
      case "start": return (
        <td key={key} className="px-3 py-2 text-xs whitespace-nowrap" data-testid={`wbs-start-${t.id}`} onClick={(e) => (e.ctrlKey || e.metaKey) ? rowClick(e, t) : leaf && beginEdit(e, t, "start", t.start_date)}>
          {isEditing(t, "start") ? (
            <input type="date" autoFocus value={draft} onClick={(e) => e.stopPropagation()} onChange={(e) => setDraft(e.target.value)} onBlur={() => commit(t)} onKeyDown={(e) => editKeys(e, t)} className="border border-primary rounded px-1 py-0.5 text-xs bg-white text-slate-900" data-testid={`wbs-edit-start-${t.id}`} />
          ) : (
            <span className={`group inline-flex items-center gap-1 ${leaf ? "cursor-text hover:text-foreground" : ""} ${dateCls}`}>{fmtD(t.start_date)}{leaf && <Pencil className="h-2.5 w-2.5 opacity-0 group-hover:opacity-60" />}</span>
          )}
        </td>
      );
      case "end": return (
        <td key={key} className="px-3 py-2 text-xs whitespace-nowrap" data-testid={`wbs-end-${t.id}`} onClick={(e) => (e.ctrlKey || e.metaKey) ? rowClick(e, t) : leaf && !t.is_milestone && beginEdit(e, t, "end", t.end_date)}>
          {isEditing(t, "end") ? (
            <input type="date" autoFocus value={draft} onClick={(e) => e.stopPropagation()} onChange={(e) => setDraft(e.target.value)} onBlur={() => commit(t)} onKeyDown={(e) => editKeys(e, t)} className="border border-primary rounded px-1 py-0.5 text-xs bg-white text-slate-900" data-testid={`wbs-edit-end-${t.id}`} />
          ) : (
            <span className={`group inline-flex items-center gap-1 ${leaf && !t.is_milestone ? "cursor-text hover:text-foreground" : ""} ${dateCls}`}>{fmtD(t.end_date)}{overdue && " ⚠"}{leaf && !t.is_milestone && <Pencil className="h-2.5 w-2.5 opacity-0 group-hover:opacity-60" />}</span>
          )}
        </td>
      );
      case "duration": return (
        <td key={key} className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap" data-testid={`wbs-duration-${t.id}`} onClick={(e) => leaf && !t.is_milestone && t.start_date && beginEdit(e, t, "duration", t.start_date && t.end_date ? bizDaysCount(t.start_date, t.end_date) : "")}>
          {isEditing(t, "duration") ? (
            <input type="number" min="1" autoFocus value={draft} onClick={(e) => e.stopPropagation()} onChange={(e) => setDraft(e.target.value)} onBlur={() => commit(t)} onKeyDown={(e) => editKeys(e, t)} className="w-14 border border-primary rounded px-1 py-0.5 text-xs bg-white text-slate-900" data-testid={`wbs-edit-duration-${t.id}`} />
          ) : (
            <span className={`group inline-flex items-center gap-1 ${leaf && !t.is_milestone && t.start_date ? "cursor-text hover:text-foreground" : ""}`} onClick={leaf && !t.is_milestone && t.start_date ? undefined : () => onOpenTask(t)}>{durationOf(t)}{leaf && !t.is_milestone && t.start_date && <Pencil className="h-2.5 w-2.5 opacity-0 group-hover:opacity-60" />}</span>
          )}
        </td>
      );
      case "resource": return (
        <td key={key} className="px-3 py-2 text-xs cursor-pointer max-w-[160px] whitespace-normal" onClick={(e) => rowClick(e, t)} data-testid={`wbs-resource-${t.id}`}>
          {(() => {
            const who = [...new Set([t.assignee, t.responsible].map((x) => (x || "").trim()).filter(Boolean))];
            return who.length
              ? <span className="inline-flex flex-wrap gap-1">{who.map((n) => <span key={n} className="px-1.5 py-0.5 rounded bg-secondary text-[11px] font-medium text-foreground/80 whitespace-normal break-words leading-tight max-w-full">{n}</span>)}</span>
              : <span className="text-muted-foreground/50">—</span>;
          })()}
        </td>
      );
      case "pct": return (
        <td key={key} className="px-3 py-2" data-testid={`wbs-pct-${t.id}`} onClick={(e) => (e.ctrlKey || e.metaKey) ? rowClick(e, t) : leaf && beginEdit(e, t, "pct", t.percent_complete)}>
          {isEditing(t, "pct") ? (
            <input type="number" min="0" max="100" autoFocus value={draft} onClick={(e) => e.stopPropagation()} onChange={(e) => setDraft(e.target.value)} onBlur={() => commit(t)} onKeyDown={(e) => editKeys(e, t)} className="w-16 border border-primary rounded px-1 py-0.5 text-xs bg-white text-slate-900" data-testid={`wbs-edit-pct-${t.id}`} />
          ) : (
            <div className={`group inline-flex items-center gap-1 ${leaf ? "cursor-text" : ""}`}>
              <PctBar value={pct} />
              {t.owner_submitted && !t.pm_confirmed && t.proposed_percent != null && (
                <span className="text-[10px] font-bold px-1 py-0.5 rounded bg-amber-100 text-amber-700 shrink-0"
                      title={`${t.assignee || "The owner"} submitted ${t.proposed_percent}% from their card — confirm it in a True-up`}
                      data-testid={`wbs-proposed-${t.id}`}>
                  →{t.proposed_percent}%
                </span>
              )}
              {leaf && <Pencil className="h-2.5 w-2.5 opacity-0 group-hover:opacity-60" />}
            </div>
          )}
        </td>
      );
      case "trueup": return (
        <td key={key} className="px-3 py-2 whitespace-nowrap" data-testid={`wbs-trueup-cell-${t.id}`} onClick={(e) => (e.ctrlKey || e.metaKey) && rowClick(e, t)}>
          {leaf && onTrueUp && (isEditing(t, "trueup") ? (
            <span className="inline-flex items-center gap-1 rounded bg-white border border-border px-1.5 py-0.5" onClick={(e) => e.stopPropagation()} data-testid={`wbs-trueup-confirm-${t.id}`}>
              <span className="text-[10px] font-bold tabular-nums text-slate-800">{t.percent_complete ?? 0}% right?</span>
              <button type="button" onClick={() => { cancel(); onTrueUp(t, t.percent_complete ?? 0); }}
                className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`wbs-trueup-yes-${t.id}`}>Yes</button>
              <button type="button" onClick={(e) => { cancel(); beginEdit(e, t, "pct", t.percent_complete); }}
                title="No — fix the % first" className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 hover:bg-slate-200" data-testid={`wbs-trueup-no-${t.id}`}>No</button>
            </span>
          ) : t.pm_confirmed ? (
            <button type="button" onClick={(e) => beginEdit(e, t, "trueup", t.percent_complete ?? 0)} className="inline-flex items-center gap-1 text-emerald-600" title={`Trued-up ${String(t.pm_confirmed_at || "").slice(0, 10)} — click to true-up again`} data-testid={`wbs-trueup-${t.id}`}>
              <CheckCircle2 className="h-4 w-4" />
            </button>
          ) : (
            <button type="button" onClick={(e) => beginEdit(e, t, "trueup", t.percent_complete ?? 0)} className="text-[11px] font-semibold text-primary hover:underline" title="Confirm this task's real progress" data-testid={`wbs-trueup-${t.id}`}>True-up</button>
          ))}
        </td>
      );
      case "status": return (
        <td key={key} className="px-3 py-2 whitespace-nowrap cursor-pointer" onClick={(e) => rowClick(e, t)}><span className="text-[11px] font-semibold px-2 py-0.5 rounded" style={{ color: sm.color, background: `${sm.color}1a` }}>{sm.label}</span></td>
      );
      case "priority": return (
        <td key={key} className="px-3 py-2 whitespace-nowrap cursor-pointer" onClick={(e) => rowClick(e, t)}><span className="text-[11px] font-semibold px-2 py-0.5 rounded" style={{ color: pm.color, background: `${pm.color}1a` }}>{pm.label}</span></td>
      );
      case "links": return (
        <td key={key} className="px-3 py-2 whitespace-nowrap" onClick={(e) => (e.ctrlKey || e.metaKey) && rowClick(e, t)}>
          {onLinkTask && (
            <button type="button" onClick={(e) => { e.stopPropagation(); onLinkTask(t); }}
              className={`inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded transition-colors ${(t.dependencies || []).length ? "text-primary bg-primary/10 hover:bg-primary/20" : "text-muted-foreground hover:text-primary hover:bg-primary/10"}`}
              title={(t.dependencies || []).length ? `${t.dependencies.length} predecessor link(s) — click to edit` : "Link to other tasks/milestones"}
              data-testid={`wbs-link-${t.id}`}>
              <Link2 className="h-3 w-3" />{(t.dependencies || []).length || "+"}
            </button>
          )}
        </td>
      );
      default: return null;
    }
  };

  const renderRow = (t, phaseIdx, num, depth, color, phaseName, siblings = [], idx = 0) => {
    const kids = childrenOf(t.id).sort((a, b) => (a.order || 0) - (b.order || 0));
    const leaf = kids.length === 0;
    const overdue = !isDone(t) && t.end_date && t.end_date < today;
    const id = t.is_milestone ? msIds[t.id] : `${phaseIdx + 1}.${num}`;
    const pct = rowPct(t);
    const sm = statusMeta(t.status);
    const pm = priorityMeta(t.priority);
    const dateCls = overdue ? "text-white font-bold" : "text-muted-foreground";
    const draggable = !!onDropTask;
    const hovered = hover && hover.id === t.id ? hover.mode : null;
    const pendingProposal = t.owner_submitted && !t.pm_confirmed && t.proposed_percent != null;
    const isSummary = t.is_summary || (kids.length > 0 && !t.is_milestone);
    if (msOnly && leaf && !t.is_milestone && !t.is_summary) return null;
    const isCollapsed = collapsedIds.has(t.id);
    const ctx = { t, id, depth, color, kids, leaf, overdue, pct, sm, pm, dateCls, draggable, siblings, idx, isSummary, isCollapsed };

    return (
      <Fragment key={t.id}>
        <tr className={`group/wbsrow border-t ${dragId === t.id ? "opacity-40" : ""} ${pendingProposal ? "bg-amber-200 hover:bg-amber-300 [&_.text-muted-foreground]:text-amber-800" : overdue ? "bg-red-600 text-white hover:bg-red-700 [&_.text-muted-foreground]:text-red-100" : "hover:bg-secondary/40"} ${hovered === "nest" ? "bg-primary/10" : ""} ${hovered === "before" ? "border-t-2 border-t-primary" : "border-border"}`} data-testid={`wbs-row-${t.id}`}
          draggable={draggable}
          onDragStart={draggable ? (e) => { e.dataTransfer.setData("text/plain", t.id); e.dataTransfer.effectAllowed = "move"; setDragId(t.id); } : undefined}
          onDragEnd={draggable ? () => { setDragId(null); setHover(null); } : undefined}
          onDragOver={draggable ? (e) => {
            if (!e.dataTransfer.types.includes("text/plain")) return;
            e.preventDefault();
            const rect = e.currentTarget.getBoundingClientRect();
            const mode = e.clientY - rect.top < rect.height * 0.35 ? "before" : "nest";
            setHover((h) => (h && h.id === t.id && h.mode === mode ? h : { id: t.id, mode }));
          } : undefined}
          onDragLeave={draggable ? () => setHover((h) => (h && h.id === t.id ? null : h)) : undefined}
          onDrop={draggable ? (e) => {
            e.preventDefault();
            const src = e.dataTransfer.getData("text/plain");
            const rect = e.currentTarget.getBoundingClientRect();
            const mode = e.clientY - rect.top < rect.height * 0.35 ? "before" : "nest";
            setDragId(null);
            setHover(null);
            if (!src || src === t.id) return;
            if (selected.has(src) && selected.size > 1) {
              onDropTask(orderedSelected.filter((x) => x !== t.id), t, phaseName, mode === "nest" ? "before" : mode);
              clearSelection();
            } else onDropTask(src, t, phaseName, mode);
          } : undefined}>
          {visibleOrder.map((key) => renderCell(key, ctx))}
        </tr>
        {!isCollapsed && kids.map((k, i) => renderRow(k, phaseIdx, `${num}.${i + 1}`, depth + 1, color, phaseName, kids, i))}
      </Fragment>
    );
  };

  const customized = colOrder.join() !== DEFAULT_ORDER.join();

  return (
    <div className="space-y-4" data-testid="wbs-table">
      {onBulkMove && selected.size > 0 && (
        <div className="sticky top-16 z-30 flex items-center gap-3 flex-wrap rounded-lg border border-primary/40 bg-blue-50/95 backdrop-blur px-3 py-2 shadow-sm" data-testid="wbs-bulk-bar">
          <span className="text-xs font-bold text-blue-900">{selected.size} selected</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline" data-testid="wbs-bulk-move-trigger">
                <FolderInput className="h-3.5 w-3.5" /> Move to phase <ChevronDown className="h-3 w-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {phases.map((p) => (
                <DropdownMenuItem key={p} onClick={() => { onBulkMove(orderedSelected, p); clearSelection(); }} className="gap-2 cursor-pointer" data-testid={`wbs-bulk-move-${p}`}>
                  <span className="h-2 w-2 rounded-full shrink-0" style={{ background: phaseColors[p] || "#0044FF" }} /> {p}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {onBulkLink && selected.size > 1 && (
            <button type="button" onClick={() => { onBulkLink(orderedSelected); clearSelection(); }}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
              title="Chain the selected tasks with finish-to-start links in WBS order" data-testid="wbs-bulk-link">
              <Link2 className="h-3.5 w-3.5" /> Link in sequence
            </button>
          )}
          {onBulkUnlink && selected.size > 1 && (
            <button type="button" onClick={() => { onBulkUnlink(orderedSelected); clearSelection(); }}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:underline"
              title="Remove any dependency links between the selected tasks" data-testid="wbs-bulk-unlink">
              <Unlink className="h-3.5 w-3.5" /> Unlink
            </button>
          )}
          <span className="text-[11px] text-muted-foreground hidden sm:inline">Ctrl-click rows to select more · drag any selected row to move them all together</span>
          <button type="button" onClick={clearSelection} className="ml-auto text-xs font-semibold text-muted-foreground hover:text-foreground" data-testid="wbs-bulk-clear">Clear</button>
        </div>
      )}
      <div className="flex justify-end items-center gap-3 -mb-2">
        {customized && (
          <button type="button" onClick={() => saveColOrder(DEFAULT_ORDER)}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground"
            data-testid="wbs-reset-columns">
            <RotateCcw className="h-3 w-3" /> Reset column order
          </button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground" data-testid="wbs-rows-menu">
              <Layers className="h-3 w-3" /> Rows{msOnly ? " (milestones)" : ""}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={expandAllRows} data-testid="wbs-expand-all">Expand all rows</DropdownMenuItem>
            <DropdownMenuItem onClick={collapseAllRows} data-testid="wbs-collapse-all">Collapse all rows (summary level)</DropdownMenuItem>
            <DropdownMenuCheckboxItem checked={msOnly} onCheckedChange={() => setMsOnly((v) => !v)} onSelect={(e) => e.preventDefault()} data-testid="wbs-ms-only">
              Milestones & summaries only
            </DropdownMenuCheckboxItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {onSummaryColor && (
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground" data-testid="wbs-summary-color">
            Summary fill
            <SwatchPicker value={sumColor} onChange={onSummaryColor} title="Summary row color" testid="wbs-summary-swatch" />
          </span>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground" data-testid="wbs-columns-menu">
              <Columns3 className="h-3 w-3" /> Columns{hiddenCols.length ? ` (${hiddenCols.length} hidden)` : ""}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            {colOrder.map((key) => (
              <DropdownMenuCheckboxItem key={key} checked={!hiddenCols.includes(key)} disabled={key === "task"}
                onCheckedChange={() => toggleCol(key)} onSelect={(e) => e.preventDefault()}
                data-testid={`wbs-coltoggle-${key}`}>
                {COLUMNS[key].label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {phases.map((g, phaseIdx) => {
        const all = tasks.filter((t) => t.phase === g);
        const top = all.filter((t) => !t.parent_id || !all.some((p) => p.id === t.parent_id)).sort((a, b) => (a.order || 0) - (b.order || 0));
        const color = phaseColors[g] || "#0044FF";
        const open = !collapsed[g];
        const leaves = all.filter((t) => !all.some((c) => c.parent_id === t.id));
        const scored = all.filter((t) => !t.is_summary);
        const phasePct = scored.length ? progressPct(scored, progressStyle) : 0;
        const launchDates = all.filter((t) => t.is_launch).map((t) => t.end_date || t.start_date).filter(Boolean).sort();
        const launchDate = launchDates.length ? launchDates[launchDates.length - 1] : null;
        return (
          <div key={g} className="border border-border rounded-lg overflow-hidden">
            <div className="w-full flex items-center gap-2 px-4 py-2.5 border-b border-border" style={{ background: `${color}0d` }}>
              <button type="button" onClick={() => onToggle(g)} className="flex items-center gap-2 flex-1 min-w-0 text-left hover:brightness-95 transition-all" data-testid={`wbs-phase-toggle-${g}`}>
                {open ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
                <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: color }} />
                {phaseEdit !== g && (
                  <>
                    <span className="font-display font-bold truncate">{g}</span>
                    <span className="text-xs text-muted-foreground" title={`${scored.length} tasks & milestones in this phase (summary rows excluded)`} data-testid={`wbs-phase-count-${g}`}>{scored.length}</span>
                    {all.length > 0 && (
                      <span className="inline-flex items-center gap-1.5 shrink-0 ml-1" title={`${phasePct}% complete across ${scored.length} item${scored.length === 1 ? "" : "s"}`} data-testid={`wbs-phase-pct-${g}`}>
                        <span className="w-16 h-1.5 rounded-full bg-black/10 overflow-hidden hidden sm:block">
                          <span className="block h-full rounded-full transition-all" style={{ width: `${phasePct}%`, background: phasePct >= 100 ? "#10B981" : color }} />
                        </span>
                        <span className={`text-[11px] font-extrabold tabular-nums ${phasePct >= 100 ? "text-emerald-600" : "text-muted-foreground"}`}>{phasePct}%</span>
                      </span>
                    )}
                    {launchDate && (
                      <span className="inline-flex items-center gap-1 shrink-0 ml-1 text-[11px] font-bold text-amber-600 bg-amber-500/10 rounded-full px-2 py-0.5" title={`Launch date for this phase: ${launchDate}`} data-testid={`wbs-phase-launch-${g}`}>
                        <span className="text-amber-500">★</span> Launch {fmtD(launchDate)}
                      </span>
                    )}
                  </>
                )}
              </button>
              {phaseEdit === g && (
                <input autoFocus value={phaseDraft} onChange={(e) => setPhaseDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitPhaseRename(g); } else if (e.key === "Escape") { setPhaseEdit(null); } }}
                  onBlur={() => commitPhaseRename(g)}
                  className="flex-1 border border-primary rounded px-2 py-1 text-sm font-bold bg-background" data-testid={`wbs-phase-rename-input-${g}`} />
              )}
              {onRenamePhase && phaseEdit !== g && (
                <>
                  <button type="button" title="Expand all rows in this phase" onClick={() => collapsePhaseRows(g, false)} className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground" data-testid={`wbs-phase-expand-${g}`}>
                    <ChevronsDown className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" title="Collapse all rows in this phase to summary level" onClick={() => collapsePhaseRows(g, true)} className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground" data-testid={`wbs-phase-collapse-${g}`}>
                    <ChevronsUp className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
              {onRenamePhase && phaseEdit !== g && (
                <button type="button" title="Rename phase" onClick={() => { setPhaseEdit(g); setPhaseDraft(g); }} className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground" data-testid={`wbs-phase-rename-${g}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
              {onDuplicatePhase && (
                <button type="button" title="Duplicate this phase and all its tasks" onClick={() => onDuplicatePhase(g)} className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground" data-testid={`wbs-phase-duplicate-${g}`}>
                  <Copy className="h-3.5 w-3.5" />
                </button>
              )}
              {onSaveTemplate && (
                <button type="button" title="Save this phase to the Library for reuse in any project" onClick={() => onSaveTemplate(g)} className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground" data-testid={`wbs-phase-template-${g}`}>
                  <BookMarked className="h-3.5 w-3.5" />
                </button>
              )}
              {onMovePhase && (
                <span className="inline-flex items-center shrink-0">
                  <button type="button" disabled={phaseIdx === 0} title="Move phase up" onClick={() => onMovePhase(g, -1)}
                    className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground disabled:opacity-25 disabled:cursor-default" data-testid={`wbs-phase-up-${g}`}>
                    <ArrowUp className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" disabled={phaseIdx === phases.length - 1} title="Move phase down" onClick={() => onMovePhase(g, 1)}
                    className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground disabled:opacity-25 disabled:cursor-default" data-testid={`wbs-phase-down-${g}`}>
                    <ArrowDown className="h-3.5 w-3.5" />
                  </button>
                </span>
              )}
              {onPhaseColor && (
                <SwatchPicker value={color} onChange={(c) => onPhaseColor(g, c)} title="Change this phase's color" testid={`wbs-phase-color-${g}`} />
              )}
              {onDeletePhase && (
                <button type="button" title="Delete this phase and all its tasks" onClick={() => onDeletePhase(g)} className="p-1 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600" data-testid={`wbs-phase-delete-${g}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {open && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-secondary/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                      {visibleOrder.map((key) => {
                        const thProps = {
                          draggable: true,
                          onDragStart: (e) => { e.dataTransfer.setData(COL_MIME, key); e.dataTransfer.effectAllowed = "move"; setDragCol(key); },
                          onDragEnd: () => { setDragCol(null); setHoverCol(null); },
                          onDragOver: (e) => { if (e.dataTransfer.types.includes(COL_MIME)) { e.preventDefault(); setHoverCol(key); } },
                          onDragLeave: () => setHoverCol((h) => (h === key ? null : h)),
                          onDrop: (e) => { if (e.dataTransfer.types.includes(COL_MIME)) { e.preventDefault(); setHoverCol(null); dropCol(key); setDragCol(null); } },
                          title: "Drag to reorder columns",
                          className: `px-3 py-2 font-semibold cursor-grab select-none ${COLUMNS[key].width} ${dragCol === key ? "opacity-40" : ""} ${hoverCol === key && dragCol !== key ? "border-l-2 border-l-primary bg-primary/10" : ""}`,
                        };
                        if (key === "task") return (
                          <Fragment key="task">
                            <th {...thProps} className={`${thProps.className} w-40`} data-testid="wbs-col-milestone">Milestone</th>
                            <th {...thProps} className={`${thProps.className} min-w-[240px]`} data-testid="wbs-col-task">Task</th>
                          </Fragment>
                        );
                        return (
                          <th key={key} {...thProps} data-testid={`wbs-col-${key}`}>
                            {COLUMNS[key].label}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {top.map((t, i) => renderRow(t, phaseIdx, i + 1, 0, color, g, top, i))}
                    {onAddTask && (
                      <tr className="border-t border-border bg-secondary/20"
                        onDragOver={(e) => { if (e.dataTransfer.types.includes("text/plain")) e.preventDefault(); }}
                        onDrop={(e) => { e.preventDefault(); const src = e.dataTransfer.getData("text/plain"); setDragId(null); setHover(null); if (!src) return; if (selected.has(src) && selected.size > 1) { onDropTask?.(orderedSelected, null, g, "append"); clearSelection(); } else onDropTask?.(src, null, g, "append"); }}>
                        <td className="px-3 py-1.5" />
                        <td colSpan={Math.max(1, visibleOrder.length)} className="px-3 py-1.5">
                          <div className="flex items-center gap-3">
                            <button type="button" onClick={() => onAddTask(g, false)}
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
                              data-testid={`wbs-add-task-${g}`}>
                              <Plus className="h-3.5 w-3.5" /> Add task
                            </button>
                            <span className="text-muted-foreground/40 text-xs">|</span>
                            <button type="button" onClick={() => onAddTask(g, true)}
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground hover:underline"
                              data-testid={`wbs-add-milestone-${g}`}>
                              <span className="text-[10px] leading-none">▼</span> Add milestone
                            </button>
                            <span className="text-muted-foreground/40 text-xs">|</span>
                            <button type="button" onClick={() => onAddTask(g, false, true)}
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground hover:underline"
                              data-testid={`wbs-add-summary-${g}`}>
                              <Layers className="h-3 w-3" /> Add summary
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
      {onAddPhase && (
        <div className="pt-1">
          {addingPhase ? (
            <div className="flex items-center gap-2">
              <input autoFocus value={newPhase} onChange={(e) => setNewPhase(e.target.value)} placeholder="New phase name"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitNewPhase(); } else if (e.key === "Escape") { setAddingPhase(false); setNewPhase(""); } }}
                className="border border-primary rounded px-2 py-1.5 text-sm w-64 bg-background" data-testid="wbs-new-phase-input" />
              <button type="button" className="text-xs font-semibold text-primary hover:underline" onMouseDown={(e) => { e.preventDefault(); submitNewPhase(); }} data-testid="wbs-new-phase-save">Add</button>
              <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onMouseDown={(e) => { e.preventDefault(); setAddingPhase(false); setNewPhase(""); }}>Cancel</button>
            </div>
          ) : (
            <button type="button" onClick={() => setAddingPhase(true)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline" data-testid="wbs-add-phase">
              <Plus className="h-3.5 w-3.5" /> Add phase
            </button>
          )}
          {onImportPhase && !addingPhase && (
            <button type="button" onClick={onImportPhase} className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline ml-4" data-testid="wbs-import-phase">
              <BookMarked className="h-3.5 w-3.5" /> Import phase from Library
            </button>
          )}
        </div>
      )}
    </div>
  );
}
